import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import DescriptionBox from '../../descriptionBox/DescriptionBox';
import { CoachingTabs } from '../../../utils/typeConstants';
import { getMetricTypeConfig, METRIC_TYPE_CURRENCY, METRIC_TYPE_NUMERIC } from '../../../features/coachingAction/coachingActionPlanTypes';
import { metricValueFormat } from '../../../utils/metricValueFormat';
import { useUpdateSessionOverrideMutation } from '../../../features/coachingAction/coachingActionSlice';
import { selectCurrentEmpid } from '../../../pages/login/authSlice';
import './overridePerformancePopup.scss';


interface OverridePerformancePopupProps {
    visible: boolean;
    metricName?: string;
    currentPerformance?: number | null;
    onCancel?: () => void;
    metricTypeId?: number;
    metricTypeName?: string;
    recognize: boolean;
    CAPCurrentSession?: any;
    onContinueToRecognition?: () => void;
    coachingActionPlanId?: number;
}

export const OverridePerformancePopup: React.FC<OverridePerformancePopupProps> = ({
    visible,
    metricName,
    currentPerformance,
    onCancel,
    metricTypeId,
    metricTypeName,
    recognize,
    CAPCurrentSession,
    onContinueToRecognition,
    coachingActionPlanId
}) => {
    const [overrideValue, setOverrideValue] = useState<string>('');
    const [overrideReason, setOverrideReason] = useState<string>('');
    const [errors, setErrors] = useState<{ value?: string; reason?: string }>({});
    const [updateSessionOverride] = useUpdateSessionOverrideMutation();

    const typeConfig = getMetricTypeConfig(metricTypeId, metricTypeName);
    const loggedUser = useSelector(selectCurrentEmpid);

    useEffect(() => {
        if (recognize) {
            setOverrideValue('');
            setOverrideReason('');
            return;
        }

        const overrideVal = CAPCurrentSession?.metricCurrentValueOverride;

        let formattedOverride = '';
        if (overrideVal != null) {
            formattedOverride = metricTypeName === 'Percentage'
                ? (overrideVal * 100).toFixed(2)
                : overrideVal.toString();
        }

        setOverrideValue(formattedOverride);
        setOverrideReason(CAPCurrentSession?.metricCurrentValueOverrideReason ?? '');
    }, [CAPCurrentSession, visible]);

    const validate = () => {
        if (recognize) {
            return true;
        }
        const newErrors: { value?: string; reason?: string } = {};
        if (!overrideValue) newErrors.value = 'Override value is required';
        if (!overrideReason.trim()) newErrors.reason = 'Override reason is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const hasDataChanged = () => {
        const originalValue = CAPCurrentSession?.metricCurrentValueOverride;
        const originalReason = CAPCurrentSession?.metricCurrentValueOverrideReason ?? '';

        let originalFormatted = '';
        if (originalValue != null) {
            originalFormatted = metricTypeName === 'Percentage'
                ? (originalValue * 100).toFixed(2)
                : originalValue.toString();
        }

        return overrideValue !== originalFormatted || overrideReason !== originalReason;
    };

    const handleContinueToRecognition = async () => {
        if (hasDataChanged()) {
            // validate only if data changed
            const newErrors: { value?: string; reason?: string } = {};
            if (!overrideValue) newErrors.value = 'Override value is required';
            if (!overrideReason.trim()) newErrors.reason = 'Override reason is required';

            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                return;
            }

            try {
                await updateSessionOverride({
                    coachingActionPlanSessionId: CAPCurrentSession?.coachingActionPlanSessionId,
                    overrideValue: metricTypeName === 'Percentage'
                        ? parseFloat(overrideValue) / 100
                        : parseFloat(overrideValue),
                    employeeNumber: loggedUser?.toString(),
                    overrideReason,
                    isRecognize: recognize,
                    coachingActionPlanId

                }).unwrap();

                toast.success('Session Override successfully.');
                setErrors({});
            } catch (error) {
                console.error('Update Session Override failed:', error);
                toast.error('Override failed to save. Please try again.');
                return;
            }
        }

        // whether data changed or not, open shoutout
        handleCancel();
        onContinueToRecognition?.();
    };

    const handleSave = async () => {
        if (validate()) {
            try {
                await updateSessionOverride({
                    coachingActionPlanSessionId: CAPCurrentSession?.coachingActionPlanSessionId,
                    overrideValue: metricTypeName === 'Percentage'
                        ? parseFloat(overrideValue) / 100
                        : parseFloat(overrideValue),
                    employeeNumber: loggedUser?.toString(),
                    overrideReason,
                }).unwrap();

                toast.success('Session Override successfully.');
                setErrors({});
                handleCancel();
            } catch (error) {
                console.error('Update Session Override failed:', error);
                toast.error('Override failed to save. Please try again.');
            }
        }
    };

    const handleCancel = () => {
        setOverrideValue('');
        setOverrideReason('');
        setErrors({});
        onCancel?.();
    };

    const handlePerformanceOverrideChange = (value: string) => {
        if (value === '' || typeConfig.regex.test(value)) {
            setOverrideValue(value);
        }
    };

    const performanceCommitmentKeyFilter = metricTypeId === METRIC_TYPE_NUMERIC
        ? /^-?\d*$/
        : /^-?\d*(\.\d*)?$/;

    const header = () => {
        return (
            <>
                <div className="override-performance__icon-wrapper">
                    <span className="override-performance__icon-text">?</span>
                </div>

                <h2 className="override-performance__title">
                    {recognize ? 'Recognize and Close' : 'Override Performance'}
                </h2>
            </>
        );
    };

    return (
        <Dialog
            visible={visible}
            onHide={handleCancel}
            header={header}
            className="session-modal"
            style={{ width: '900px', maxHeight: '90vh' }}
            contentStyle={{ maxHeight: '75vh', overflowY: 'auto' }}
        >
            <div className="session-modal__body">

                {recognize && (
                    <p className="override-performance__recognize-note">
                        Performance has not met Commitment. We recommend to Follow Up, but you can provide
                        a justification to recognize and close instead (Keep in mind that if you enter an override,
                        you need to provide an Override reason).
                    </p>
                )}

                <hr className="override-performance__divider" />

                <div className="override-performance__info-row">
                    <div className="override-performance__info-col">
                        <p className="override-performance__info-label">
                            Current performance on <em>{metricName}</em>
                        </p>
                        <div className="override-performance__value-row">
                            <span className="override-performance__value-badge">
                                {metricValueFormat(currentPerformance, metricTypeName ?? '')}
                            </span>
                            <span className="override-performance__value-target">
                                / {metricValueFormat(CAPCurrentSession?.metricTargetValue, metricTypeName ?? '')}
                            </span>
                        </div>
                    </div>
                </div>

                <hr className="override-performance__divider" />

                <div className="override-performance__field-group">
                    <div className="override-performance__field-label-row">
                        <label htmlFor="override-current-performance" className="override-performance__field-label">
                            Override Current Performance*
                        </label>
                        <div className="override-performance__input-with-unit">
                            {metricTypeId === METRIC_TYPE_CURRENCY && (
                                <span className="input-addon input-addon--prefix">$</span>
                            )}
                            <InputText
                                id="override-current-performance"
                                value={overrideValue}
                                onChange={(e) => {
                                    handlePerformanceOverrideChange(e.target.value);
                                    if (errors.value) setErrors((prev) => ({ ...prev, value: undefined }));
                                }}
                                className={`override-performance__input ${errors.value ? 'p-invalid' : ''}`}
                                placeholder={typeConfig.placeholder}
                                keyfilter={performanceCommitmentKeyFilter}
                                style={{ borderRadius: '20px' }}
                            />
                            {typeConfig.suffix && metricTypeId !== METRIC_TYPE_CURRENCY && (
                                <span className="input-addon input-addon--suffix">{typeConfig.suffix}</span>
                            )}
                        </div>
                        <p className="override-performance__field-hint">{typeConfig.hint}</p>
                    </div>
                    {errors.value && <p className="override-performance__error-text">{errors.value}</p>}
                </div>

                <div className="override-performance__field-group">
                    <DescriptionBox
                        title="Override Reason*"
                        responseDescription={overrideReason}
                        value={overrideReason}
                        limit={1000}
                        onChange={(val: string) => setOverrideReason(val)}
                        via={CoachingTabs.CoachingActionPlan}
                        placeholder="Override Reason"
                        className={errors.reason && !overrideReason?.trim() ? 'p-invalid' : ''}
                    />
                    {errors.reason && <p className="override-performance__error-text">{errors.reason}</p>}
                </div>

                <hr className="override-performance__divider" />

                <div className="override-performance__button-row">
                    <Button
                        label="Cancel"
                        className="btn-secondary me-2"
                        onClick={handleCancel}
                    />
                    {recognize && (
                        <Button
                            label="Continue to Recognition"
                            className="btn-primary"
                            onClick={handleContinueToRecognition}
                        />
                    )}
                    {!recognize && (
                        <Button
                            label="Save"
                            className="btn-primary"
                            onClick={handleSave}
                        />
                    )}
                </div>
            </div>
        </Dialog>
    );
};